import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import EnvironmentSwitcher from './EnvironmentSwitcher';
import LanguageSelector from './LanguageSelector';
import ClinicSwitcher from './ClinicSwitcher';
import { getEnabledNavItems, isPathActive } from '../config/navigation';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  LightningIcon
} from '../assets/icons';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, hasModule, clid } = useAuth();
  const { t } = useLanguage();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Get enabled navigation items from config, filtered by user's module permissions and clid
  const navItems = getEnabledNavItems(hasModule, clid);

  // Initialize sidebar state from localStorage or default to false (expanded)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState ? JSON.parse(savedState) : false;
  });

  // Update localStorage whenever sidebar state changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-white shadow-md z-50">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center">
            <h1 className="text-xl font-bold text-[#075e54]">Nexi Dashboard</h1>
          </div>
          <div className="flex items-center space-x-4 relative">
            {/* Clinic Switcher */}
            <ClinicSwitcher />

            {/* Language Selector */}
            <LanguageSelector />

            {/* Environment Switcher */}
            {/* <EnvironmentSwitcher /> */}

            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 focus:outline-none"
              >
                {/* <div className="w-8 h-8 rounded-full bg-[#075e54] flex items-center justify-center text-white">
                  {currentUser?.username?.charAt(0).toUpperCase() || 'U'}
                </div> */}
                <span className="text-gray-700">{currentUser?.username || 'User'}</span>
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20">
                  <div className="px-4 py-2 text-sm text-gray-700 border-b">
                    <p className="font-medium">{currentUser?.username}</p>
                    <p className="text-gray-500 text-xs truncate">{currentUser?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {t('nav.signOut')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex pt-16">
        {/* Sidebar */}
        <div
          className={`${sidebarCollapsed ? 'w-20' : 'w-64'} h-[calc(100vh-4rem)] bg-white shadow-md fixed transition-all duration-300`}
        >
          {/* Logo and Toggle Button */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : ''}`}>
              {/* Logo SVG */}
              <div className="w-10 h-10 rounded-md bg-[#075e54] flex items-center justify-center text-white">
                <LightningIcon />
              </div>
              {!sidebarCollapsed && <span className="ml-2 font-bold text-[#075e54]">Nexi</span>}
            </div>
            <button
              onClick={toggleSidebar}
              className={`text-gray-500 hover:text-gray-700 focus:outline-none ${sidebarCollapsed ? 'hidden' : ''}`}
            >
              <ChevronLeftIcon />
            </button>
          </div>

          {/* Sidebar Toggle Button (only visible when collapsed) */}
          {sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="w-full flex justify-center py-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <ChevronRightIcon />
            </button>
          )}

          <div className="p-4">
            <nav className="space-y-2">
              {navItems.map((navItem) => {
                const IconComponent = navItem.icon;
                const isActive = isPathActive(navItem.path, location.pathname, navItem.exactPath);

                return (
                  <Link
                    key={navItem.id}
                    to={navItem.path}
                    className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                      isActive ? 'bg-gray-100' : ''
                    }`}
                  >
                    <IconComponent />
                    {!sidebarCollapsed && <span className="ml-2">{t(navItem.labelKey)}</span>}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className={`${sidebarCollapsed ? 'ml-20' : 'ml-64'} flex-1 p-3 transition-all duration-300`}>
          <div className="grid grid-cols-1 gap-3">
            <div className="bg-grey rounded-lg">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
